import React, { memo, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Navigation from '../shared/components/Navigation';
import Footer from '../shared/components/Footer';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import TalentCartPrompt from '../shared/components/TalentCartPrompt';

const RTLWrapper: React.FC<{ children: React.ReactNode }> = memo(({ children }) => {
  const { language } = useLanguage();
  return (
    <div dir={language === 'ar' ? 'rtl' : 'ltr'} lang={language}>
      {children}
    </div>
  );
});

// Admin pages manage their own dense, scrollable layout (sidebar/table-heavy) —
// the public marketing footer doesn't belong at the bottom of those screens.
const isAdminRoute = (pathname: string) =>
  pathname === '/dashboard/admin' || pathname.startsWith('/admin/');

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();

  // React Router doesn't reset scroll position on navigation the way a full
  // page load does — without this, a route change lands wherever the
  // previous page happened to be scrolled to (e.g. opening Talents mid-page
  // because Home was scrolled down when the link was clicked).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <RTLWrapper>
      <div className="app-container min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1">
          <Suspense fallback={<LoadingSpinner />}>
            {children}
          </Suspense>
        </main>
        {!isAdminRoute(pathname) && <Footer />}
        <TalentCartPrompt />
      </div>
    </RTLWrapper>
  );
};

export default MainLayout;
